import React, { createContext, useContext, useState, useCallback } from 'react';
import { getAccountLockoutStatus, type AccountLockoutStatus } from '@/api/accountLockout';

interface AccountLockoutContextValue {
  lockoutStatus: AccountLockoutStatus | null;
  isLoading: boolean;
  checkLockoutStatus: () => Promise<void>;
  setLockoutFromResponse: (status: AccountLockoutStatus) => void;
}

const AccountLockoutContext = createContext<AccountLockoutContextValue | undefined>(undefined);

export function AccountLockoutProvider({ children }: { children: React.ReactNode }) {
  const [lockoutStatus, setLockoutStatus] = useState<AccountLockoutStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkLockoutStatus = useCallback(async () => {
    try {
      const status = await getAccountLockoutStatus();
      setLockoutStatus(status);
    } catch (error) {
      console.error('Failed to check lockout status:', error);
      // On error, assume not locked to avoid blocking users
      setLockoutStatus({ is_locked: false });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setLockoutFromResponse = useCallback((status: AccountLockoutStatus) => {
    setLockoutStatus(status);
    setIsLoading(false);
  }, []);

  return (
    <AccountLockoutContext.Provider
      value={{ lockoutStatus, isLoading, checkLockoutStatus, setLockoutFromResponse }}
    >
      {children}
    </AccountLockoutContext.Provider>
  );
}

export function useAccountLockout() {
  const context = useContext(AccountLockoutContext);
  if (context === undefined) {
    throw new Error('useAccountLockout must be used within an AccountLockoutProvider');
  }
  return context;
}
